<script setup lang="ts">
/**
 * ⌘K / Ctrl+K: jump anywhere, search knowledge, open a tool, ask Schorschi.
 * One input, one keyboard-driven list (combobox pattern), results grouped by kind.
 */
import { ArrowRight, BookOpen, CornerDownLeft, FileText, MessageCircle, Search, Wrench } from "@lucide/vue";
import { refDebounced, useEventListener } from "@vueuse/core";
import { computed, nextTick, ref, watch, type Component } from "vue";
import { useRouter } from "vue-router";
import { useKnowledgeSearch } from "@/api/knowledge";
import { useMyTools } from "@/api/me";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { openSchorschi } from "@/components/schorschi/panel";
import { Kbd } from "@/components/ui/kbd";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { cn } from "@/lib/utils";
import { navDestinations } from "./nav";

const open = defineModel<boolean>("open", { default: false });

interface Entry {
  id: string;
  group: string;
  label: string;
  hint?: string;
  icon: Component;
  to?: string;
  /** Instead of navigating (opening the Schorschi panel). */
  run?: () => void;
}

const router = useRouter();
const { allows, can } = useSession();
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const query = ref("");
const debounced = refDebounced(query, 180);
const activeIndex = ref(0);
const input = ref<HTMLInputElement | null>(null);

const tools = useMyTools();
const knowledgeEnabled = computed(() => can("knowledge.read"));
const search = useKnowledgeSearch(() => ({ q: knowledgeEnabled.value && debounced.value.length >= 2 ? debounced.value : "", limit: 6, instant: true }));

useEventListener(window, "keydown", (e: KeyboardEvent) => {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
    e.preventDefault();
    open.value = !open.value;
  }
});

watch(open, async (value) => {
  if (value) {
    query.value = "";
    activeIndex.value = 0;
    await nextTick();
    input.value?.focus();
  }
});

const entries = computed<Entry[]>(() => {
  const q = query.value.trim().toLowerCase();
  const out: Entry[] = [];
  const pages = navDestinations().filter((i) => allows(i.requires));
  for (const item of pages) {
    if (!q || item.label.toLowerCase().includes(q)) out.push({ id: `nav:${item.to}`, group: "Go to", label: item.label, icon: item.icon, to: item.to });
  }
  if (can("schorschi.exec")) {
    const asked = query.value.trim();
    out.push(
      asked
        ? { id: "schorschi:ask", group: "Schorschi", label: `Ask Schorschi: “${asked}”`, icon: MessageCircle, run: () => openSchorschi({ ask: asked }) }
        : { id: "schorschi:open", group: "Schorschi", label: "Ask Schorschi", hint: isMac ? "⌘J" : "Ctrl+J", icon: MessageCircle, run: () => openSchorschi() },
    );
  }
  if (q) {
    for (const hit of search.data.value?.hits ?? []) {
      const to = hit.kind === "entity" && hit.type && hit.slug ? `/k/${hit.type}/${hit.slug}` : hit.id ? `/k/doc/${hit.id}` : null;
      if (to) out.push({ id: `k:${hit.uri}`, group: t.nav.knowledge, label: hit.title, hint: hit.type ?? t.knowledge.document, icon: hit.kind === "entity" ? BookOpen : FileText, to });
    }
    if (knowledgeEnabled.value) {
      out.push({ id: "k:search", group: t.nav.knowledge, label: `Search knowledge for “${query.value.trim()}”`, icon: Search, to: `/knowledge/search?q=${encodeURIComponent(query.value.trim())}` });
    }
    const matching = (tools.data.value ?? []).filter((tool) => tool.name.includes(q) || (tool.title ?? "").toLowerCase().includes(q)).slice(0, 6);
    for (const tool of matching) out.push({ id: `tool:${tool.name}`, group: t.nav.tools, label: tool.title ?? tool.name, hint: tool.name, icon: Wrench, to: `/tools/${tool.name}` });
  }
  return out;
});

const groups = computed(() => {
  const map = new Map<string, { entry: Entry; index: number }[]>();
  entries.value.forEach((entry, index) => {
    if (!map.has(entry.group)) map.set(entry.group, []);
    map.get(entry.group)!.push({ entry, index });
  });
  return [...map.entries()];
});

watch(entries, () => {
  if (activeIndex.value >= entries.value.length) activeIndex.value = 0;
});

function go(entry: Entry | undefined) {
  if (!entry) return;
  open.value = false;
  if (entry.run) entry.run();
  else if (entry.to) void router.push(entry.to);
}

function onKeydown(e: KeyboardEvent) {
  const n = entries.value.length;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    activeIndex.value = n ? (activeIndex.value + 1) % n : 0;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    activeIndex.value = n ? (activeIndex.value - 1 + n) % n : 0;
  } else if (e.key === "Enter") {
    e.preventDefault();
    go(entries.value[activeIndex.value]);
  }
}

watch(activeIndex, async (i) => {
  await nextTick();
  document.getElementById(`cmd-${i}`)?.scrollIntoView({ block: "nearest" });
});
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="top-[15%] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-xl" :show-close-button="false">
      <DialogTitle class="sr-only">Search and jump</DialogTitle>
      <DialogDescription class="sr-only">Type to search pages, knowledge and tools. Use arrow keys and Enter.</DialogDescription>
      <div class="flex items-center gap-2 border-b px-3">
        <Search class="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <input
          ref="input"
          v-model="query"
          role="combobox"
          aria-expanded="true"
          aria-controls="cmd-list"
          :aria-activedescendant="entries.length ? `cmd-${activeIndex}` : undefined"
          aria-autocomplete="list"
          :placeholder="t.knowledge.searchPlaceholder"
          class="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          @keydown="onKeydown"
        />
        <Kbd>Esc</Kbd>
      </div>
      <div id="cmd-list" role="listbox" class="max-h-[min(60vh,26rem)] overflow-y-auto p-2">
        <p v-if="entries.length === 0" class="px-3 py-8 text-center text-sm text-muted-foreground">{{ t.common.noResults }}</p>
        <div v-for="[group, items] in groups" :key="group" role="group" :aria-label="group" class="mb-1">
          <p class="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{{ group }}</p>
          <div
            v-for="{ entry, index } in items"
            :id="`cmd-${index}`"
            :key="entry.id"
            role="option"
            :aria-selected="index === activeIndex"
            :class="cn('flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm', index === activeIndex && 'bg-accent text-accent-foreground')"
            @mousemove="activeIndex = index"
            @click="go(entry)"
          >
            <component :is="entry.icon" class="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span class="truncate">{{ entry.label }}</span>
            <span v-if="entry.hint" class="truncate text-xs text-muted-foreground">{{ entry.hint }}</span>
            <CornerDownLeft v-if="index === activeIndex" class="ml-auto size-3.5 text-muted-foreground" aria-hidden="true" />
            <ArrowRight v-else class="ml-auto size-3.5 opacity-0" aria-hidden="true" />
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
